import { ReactNode, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { EmptyStateConfig, InfinitePaginatedList } from '@frontend/components';
import { InfiniteQueryResult } from '@frontend/types';
import { theme } from '@frontend/theme';
import { useDebouncedValue, useFormField } from '@frontend/design-system';
import { CompactTableContext } from '../../context/compact-table-context';
import {
  CompactTableExpandAllButton,
  CompactTableExpandRowButton,
  CompactTableActions,
  CompactTableSearch,
  CompactTableSelectAllRows,
  CompactTableSelectRow,
  CompactTableRowActions,
  CompactTableBulkActions,
} from './atoms';
import { CompactTableRow, CompactTableRowProps } from './compact-table-row';
import { useExpandableRows, useSelectableRows } from './hooks';
import { CompactTableActionsProps, CompactTableBulkActionsProps, CompactTableData } from './types';

type CompactTableProps<T extends CompactTableData, K extends Record<'rows', T[]>> = {
  // memoize bulkActions
  bulkActions: CompactTableBulkActionsProps<T>;
  // memoize tableActions
  tableActions: CompactTableActionsProps;
  toolbarElement?: React.ReactNode;
  globalSearchConfig: {
    initialValue?: string;
    placeholder?: string;
    // memoize search handler
    searchHandler: (value: string) => void;
  };
  emptyStateConfig?: Omit<EmptyStateConfig, 'description'> & { description?: ReactNode };
  isLoading: boolean;
  infiniteQueryProps: InfiniteQueryResult<K>;
  listHeight?: number | string;
  endOfListText?: ReactNode;
  tableId: string;
  isSelectable?: boolean;
} & Omit<CompactTableRowProps<T>, 'item'>;

export const CompactTable = <T extends { id: string }, K extends Record<'rows', T[]>>({
  tableId,
  toolbarElement,
  globalSearchConfig,
  emptyStateConfig,
  tableActions,
  bulkActions,
  infiniteQueryProps,
  listHeight,
  endOfListText,
  isSelectable = true,
  ...props
}: CompactTableProps<T, K>) => {
  const data = useMemo(() => infiniteQueryProps?.data?.pages.flatMap((page) => page.rows) || [], [infiniteQueryProps]);

  const { expandedRows, setExpandedRows, toggleAllRowsExpanded, allExpandedByDefault, setAllExpandedByDefault } =
    useExpandableRows();

  const { selectableRows, selectedRows, setSelectedRows, toggleSelectAllRows, areAllRowsSelected } = useSelectableRows({
    data,
    rowSelectionConfig: props.rowSelectionConfig,
  });

  // Global search logic
  const { searchHandler, placeholder, initialValue } = globalSearchConfig;
  const searchFieldProps = useFormField({ type: 'text', value: initialValue || '', placeholder });
  const debouncedValue = useDebouncedValue(searchFieldProps.value, 500);
  useEffect(() => {
    searchHandler(debouncedValue);
  }, [searchHandler, debouncedValue]);

  const values = useMemo(
    () => ({
      tableActions,
      bulkActions,
      data,
      selectedRows,
      toggleAllRowsExpanded,
      toggleSelectAllRows,
      allExpandedByDefault,
      setAllExpandedByDefault,
      selectableRows,
      areAllRowsSelected,
      searchFieldProps,
      tableId,
      isSelectable,
    }),
    [
      tableActions,
      bulkActions,
      data,
      selectedRows,
      toggleAllRowsExpanded,
      toggleSelectAllRows,
      allExpandedByDefault,
      setAllExpandedByDefault,
      selectableRows,
      areAllRowsSelected,
      searchFieldProps,
      tableId,
      isSelectable,
    ]
  );

  return (
    <CompactTableContext.Provider value={values}>
      {toolbarElement && <div css={styles.toolbar}>{toolbarElement}</div>}
      <div css={styles.table}>
        <InfinitePaginatedList
          infiniteQueryProps={infiniteQueryProps}
          renderListItem={({ listItem }) => (
            <CompactTableRow
              key={listItem.id}
              item={listItem}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              {...props}
            />
          )}
          endOfListText={endOfListText}
          height={listHeight}
          emptyStateConfig={emptyStateConfig as EmptyStateConfig}
        />
      </div>
    </CompactTableContext.Provider>
  );
};

const styles = {
  table: css`
    margin-top: ${theme.spacing(2)};
  `,
  toolbar: css`
    margin-top: ${theme.spacing(2)};
  `,
};

CompactTable.Search = CompactTableSearch;
CompactTable.SelectRow = CompactTableSelectRow;
CompactTable.SelectAll = CompactTableSelectAllRows;
CompactTable.ExpandAll = CompactTableExpandAllButton;
CompactTable.TableActions = CompactTableActions;
CompactTable.ExpandRow = CompactTableExpandRowButton;
CompactTable.RowActions = CompactTableRowActions;
CompactTable.BulkActions = CompactTableBulkActions;
